import { Send } from 'lucide-react';
import { useState } from 'react';
import RecordingRecovery from '../../components/RecordingRecovery';
import { useQueryClient } from '@tanstack/react-query';
import { invokeEdgeFunction } from '../../services/edgeFunctions';
import { discardPendingCapture, sendPendingCapture, usePendingCaptures } from '../../services/pendingCaptures';
import { queryKeys, refreshFilingViews } from '../../services/queries';
import { CaptureStorageError } from '../../services/captureStorage';
import { dangerButton, outlineButton } from '../../components/styles';

/**
 * The captures this browser still holds because the server does not have
 * them yet (made offline, or a save that failed), each with why and a way to
 * try again. They are sent by themselves when the network is back; nothing
 * here is lost by closing the page. Shown only when there is one.
 */
export default function PendingCaptures() {
  const pending = usePendingCaptures();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  if (pending.length === 0 && !error) return null;

  const retry = async (id: string) => {
    if ((await sendPendingCapture(id)) !== 'saved') return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.captures });
    invokeEdgeFunction('classify-capture', { capture_id: id })
      .catch(() => undefined) // Not filed: it waits in the Inbox, where it says so.
      .finally(() => void refreshFilingViews(queryClient));
  };

  return (
    <section className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm border border-tertiary/40" aria-labelledby="pending-heading">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
        <Send className="w-5 h-5 text-tertiary" />
        <h2 id="pending-heading" className="text-lg font-semibold">Waiting to send</h2>
        <span className="text-sm text-on-surface-variant">{pending.length}</span>
      </div>
      <p className="text-sm text-on-surface-variant mb-3">
        Saved in this browser while its site data is kept. Reopen Cerebro when online to send waiting captures.
      </p>
      {error && <p role="alert" className="text-error">{error}</p>}
      <ul className="space-y-3">
        {pending.map((capture) => (
          <li key={capture.id} className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-on-surface break-words">{capture.text || 'Voice recording'}</p>
              {capture.recording && <><p className="text-sm">Recording · {Math.round(capture.recording.durationMs / 1000)} seconds</p><RecordingRecovery ownerId={capture.userId} blob={capture.recording.blob} /></>}
              <p className="text-sm text-on-surface-variant">
                {new Date(capture.createdAt).toLocaleString()}
                {capture.confirmed ? ' · saved to Cerebro; local cleanup waiting' : capture.lastError ? ` · not sent: ${capture.lastError}` : ' · waiting to send'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => void retry(capture.id)} className={outlineButton}>Send now</button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(capture.confirmed ? 'Remove this local copy? The capture is already in Cerebro.' : 'Discard this local copy? The server may already have this capture. Check the Inbox before recording it again.')) {
                    void discardPendingCapture(capture.id).catch(error => setError(error instanceof CaptureStorageError ? error.message : 'The browser could not remove this capture. Its local copy remains.'));
                  }
                }}
                className={dangerButton}
              >
                Discard
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
